/* ==========================================================================
   SKNBODY HOMEPAGE - BLOCK 05
   File: assets/skn-home-one-question.js

   Responsibilities:
   - Validate the product idea
   - Submit through Shopify's contact form
   - Show success without reloading the page
   - Show a clear error state
   - Allow another submission
   - Preserve native form submission if fetch is unavailable
   - Support Shopify Theme Editor reloads
   ========================================================================== */

(function () {
  'use strict';

  const ROOT_SELECTOR =
    '[data-skn-home-one-question]';

  if (window.SKNHomeOneQuestion) {
    window.SKNHomeOneQuestion.initAll(document);
    return;
  }

  const sectionStates = new WeakMap();


  /* ==========================================================================
     INITIALIZE SECTION
     ========================================================================== */

  function initSection(root) {
    if (!root || sectionStates.has(root)) {
      return;
    }

    const form = root.querySelector(
      '.skn-home-one-question__form'
    );

    const fields = root.querySelector(
      '[data-one-question-fields]'
    );

    const input = root.querySelector(
      '[data-one-question-input]'
    );

    const submitButton = root.querySelector(
      '[data-one-question-submit]'
    );

    const buttonText = root.querySelector(
      '[data-one-question-button-text]'
    );

    const success = root.querySelector(
      '[data-one-question-success]'
    );

    const errorMessage = root.querySelector(
      '[data-one-question-error]'
    );

    const resetButton = root.querySelector(
      '[data-one-question-reset]'
    );

    if (
      !form ||
      !fields ||
      !input ||
      !submitButton ||
      !success
    ) {
      return;
    }

    let isSubmitting = false;
    let destroyed = false;

    function resizeInput() {
      input.style.height = 'auto';
      input.style.height = `${Math.min(input.scrollHeight, 132)}px`;
    }


    /* ------------------------------------------------------------------------
       FORM STATES
       ------------------------------------------------------------------------ */

    function setSubmitting(submitting) {
      isSubmitting = submitting;

      form.classList.toggle(
        'is-submitting',
        submitting
      );

      submitButton.disabled = submitting;

      submitButton.setAttribute(
        'aria-busy',
        submitting ? 'true' : 'false'
      );

      if (buttonText) {
        buttonText.textContent = submitting
          ? 'SENDING'
          : submitButton.dataset.defaultLabel ||
            'SHARE';
      }
    }

    function hideError() {
      if (!errorMessage) return;

      errorMessage.hidden = true;
    }

    function showError() {
      if (!errorMessage) return;

      errorMessage.hidden = false;
    }

    function showSuccess() {
      hideError();

      fields.hidden = true;
      success.hidden = false;

      form.reset();

      if (resetButton) {
        window.setTimeout(function () {
          resetButton.focus();
        }, 60);
      }
    }

    function showForm() {
      success.hidden = true;
      fields.hidden = false;

      hideError();
      setSubmitting(false);

      input.value = '';
      resizeInput();

      window.setTimeout(function () {
        input.focus();
      }, 40);
    }


    /* ------------------------------------------------------------------------
       SUBMISSION
       ------------------------------------------------------------------------ */

    async function handleSubmit(event) {
      if (
        destroyed ||
        isSubmitting
      ) {
        event.preventDefault();
        return;
      }

      const value = input.value.trim();

      if (!value) {
        event.preventDefault();

        input.setCustomValidity(
          'Please share an idea before submitting.'
        );

        input.reportValidity();
        input.focus();

        return;
      }

      input.setCustomValidity('');

      /*
        Without fetch support, Shopify handles the form using
        its normal page-submission flow.
      */

      if (!window.fetch || !window.FormData) {
        return;
      }

      event.preventDefault();

      hideError();
      setSubmitting(true);

      try {
        const formData = new FormData(form);

        const response = await fetch(
          form.action,
          {
            method:
              (form.method || 'POST').toUpperCase(),

            body: formData,

            credentials: 'same-origin',

            headers: {
              Accept: 'text/html',
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            'Shopify contact-form submission failed.'
          );
        }

        showSuccess();
      } catch (error) {
        showError();
      } finally {
        setSubmitting(false);
      }
    }


    /* ------------------------------------------------------------------------
       INPUT VALIDITY
       ------------------------------------------------------------------------ */

    function handleInput() {
      input.setCustomValidity('');
      resizeInput();

      if (!errorMessage || errorMessage.hidden) {
        return;
      }

      hideError();
    }


    /* ------------------------------------------------------------------------
       EVENTS
       ------------------------------------------------------------------------ */

    resizeInput();

    if (!submitButton.dataset.defaultLabel) {
      submitButton.dataset.defaultLabel =
        buttonText
          ? buttonText.textContent.trim()
          : 'SHARE';
    }

    form.addEventListener(
      'submit',
      handleSubmit
    );

    input.addEventListener(
      'input',
      handleInput
    );

    if (resetButton) {
      resetButton.addEventListener(
        'click',
        showForm
      );
    }


    /* ------------------------------------------------------------------------
       CLEANUP
       ------------------------------------------------------------------------ */

    function destroy() {
      destroyed = true;

      form.removeEventListener(
        'submit',
        handleSubmit
      );

      input.removeEventListener(
        'input',
        handleInput
      );

      if (resetButton) {
        resetButton.removeEventListener(
          'click',
          showForm
        );
      }

      sectionStates.delete(root);
    }

    sectionStates.set(root, {
      destroy: destroy,
      showForm: showForm
    });
  }


  /* ==========================================================================
     GLOBAL INITIALIZATION
     ========================================================================== */

  function initAll(context) {
    const scope = context || document;

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initSection(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initSection);
  }

  function destroyAll(context) {
    const scope = context || document;
    const roots = [];

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(function (root) {
        roots.push(root);
      });

    roots.forEach(function (root) {
      const state =
        sectionStates.get(root);

      if (
        state &&
        typeof state.destroy === 'function'
      ) {
        state.destroy();
      }
    });
  }


  /* ==========================================================================
     PUBLIC API
     ========================================================================== */

  window.SKNHomeOneQuestion = {
    initAll: initAll,
    destroyAll: destroyAll
  };


  /* ==========================================================================
     INITIAL PAGE LOAD
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      },
      {
        once: true
      }
    );
  } else {
    initAll(document);
  }


  /* ==========================================================================
     SHOPIFY THEME EDITOR
     ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      destroyAll(event.target);
    }
  );
})();